import CharacterCount from '@/components/form/CharacterCount'
import { DatePickerControl } from '@/components/form/DatePicker'
import FieldInfo from '@/components/form/FieldInfo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getAddressRegionOptions } from '@/lib/address-region-options'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import { cn } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { Plus } from 'lucide-react'
import { useState } from 'react'

export function PosDeliveryAddressFields({
  form,
  addressFieldName = 'deliveryAddress',
  onAddressSelected,
  onAddressLine1Change,
  onCoreAddressChange,
  className,
}) {
  const address = useStore(form.store, (state) => state.values[addressFieldName] ?? {})
  const addressKey = [address.line1, address.city, address.state, address.postal_code]
    .map((value) => String(value ?? '').trim())
    .join('|')
  const [revealedAddressKey, setRevealedAddressKey] = useState(null)
  const hasDestinationName = Boolean(String(address.name ?? '').trim())
  const isDestinationNameVisible = hasDestinationName || revealedAddressKey === addressKey
  const hasCompleteAddress = Boolean(
    address.line1 && address.city && address.state && String(address.postal_code ?? '').length === 5
  )

  return (
    <div className={cn('space-y-4 border-t pt-4', className)}>
      <form.AppField name={`${addressFieldName}.line1`}>
        {(field) => (
          <field.AddressSearch
            label="Delivery address"
            placeholder="Start typing a business or address..."
            onAddressSelected={onAddressSelected}
            onChange={onAddressLine1Change}
            maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.address_line1}
            characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_line1}
          />
        )}
      </form.AppField>

      {isDestinationNameVisible ? (
        <form.AppField name={`${addressFieldName}.name`}>
          {(field) => (
            <field.TextInput
              label="Business, funeral home, or church name (optional)"
              placeholder="For example, Rosewood Funeral Home"
              maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.address_name}
              characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_name}
              autoFocus={revealedAddressKey === addressKey}
              onChange={() => setRevealedAddressKey(addressKey)}
            />
          )}
        </form.AppField>
      ) : hasCompleteAddress ? (
        <Button
          type="button"
          variant="link"
          size="inline"
          className="min-h-11 cursor-pointer justify-start whitespace-normal py-2 text-left"
          onClick={() => setRevealedAddressKey(addressKey)}
        >
          <Plus aria-hidden="true" />
          Add business, funeral home, or church name
        </Button>
      ) : null}

      <form.AppField name={`${addressFieldName}.line2`}>
        {(field) => (
          <field.TextInput
            label="Address Line 2 (Optional)"
            placeholder="Apartment, Suite, Floor, etc."
            maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.address_line2}
            characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_line2}
          />
        )}
      </form.AppField>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <form.AppField name={`${addressFieldName}.city`}>
          {(field) => (
            <field.TextInput
              label="City"
              placeholder="City"
              maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.address_city}
              characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_city}
              onChange={(event) => onCoreAddressChange?.('city', event.target.value)}
            />
          )}
        </form.AppField>

        <form.AppField name={`${addressFieldName}.state`}>
          {(field) => (
            <field.SelectInput
              label="State"
              placeholder="State"
              options={getAddressRegionOptions(field.state.value)}
              onValueChange={(value) => onCoreAddressChange?.('state', value)}
            />
          )}
        </form.AppField>

        <form.AppField name={`${addressFieldName}.postal_code`}>
          {(field) => (
            <div className="flex w-full flex-col gap-y-2">
              <label
                htmlFor={`${addressFieldName}-postal-code`}
                className="px-1 text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Zip Code
              </label>
              <Input
                id={`${addressFieldName}-postal-code`}
                type="text"
                placeholder="Zip Code"
                maxLength={5}
                value={field.state.value ?? ''}
                onChange={(event) => {
                  const value = event.target.value.replace(/\D/g, '').slice(0, 5)
                  field.handleChange(value)
                  onCoreAddressChange?.('postal_code', value)
                }}
                onBlur={field.handleBlur}
                className="w-full"
              />
              <FieldInfo />
            </div>
          )}
        </form.AppField>
      </div>
    </div>
  )
}

export function PosFulfillmentDateField({
  form,
  fieldName,
  label,
  onDateChange,
  children,
  className,
  ...datePickerProps
}) {
  return (
    <div className={cn('space-y-4 border-t pt-4', className)}>
      <form.AppField name={fieldName}>
        {(field) => (
          <DatePickerControl
            {...datePickerProps}
            label={label}
            value={field.state.value}
            onChange={(date) => {
              field.handleChange(date)
              onDateChange?.(date)
            }}
            showFieldInfo
          />
        )}
      </form.AppField>
      {children}
    </div>
  )
}

export function PosSpecialInstructionsField({
  form,
  fieldName = 'specialInstructions',
  placeholder = 'Gate code, delivery instructions, etc.',
  className,
}) {
  return (
    <div className={cn('space-y-2 border-t pt-4', className)}>
      <form.AppField name={fieldName}>
        {(field) => (
          <div className="space-y-1">
            <field.TextareaInput
              label="Special Instructions (Optional)"
              placeholder={placeholder}
              maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.special_instructions}
              characterMaxLength={ORDER_FIELD_MAX_LENGTHS.special_instructions}
              rows={3}
            />
            <CharacterCount
              value={field.state.value}
              maxLength={ORDER_FIELD_MAX_LENGTHS.special_instructions}
            />
          </div>
        )}
      </form.AppField>
    </div>
  )
}
